import { ElCheckbox, ElPopover } from '@pharmsnap/shared/element-ui'
import type { IBAcTag, tagType } from '@pharmsnap/shared/types'
import { getLangDegraded } from '@pharmsnap/shared/utils/lang'
import { defineComponent, PropType, ref, watch } from '@vue/composition-api'
import { useLocale } from '../../../composition/useLocale'
import { GCountryFlag } from '../../ui/GCountryFlag/GCountryFlag'
import { GLimited } from '../../ui/GLimited/GLimited'
import { GTooltip } from '../../ui/GTooltip/GTooltip'
import { BAcTag } from '../tag/BAcTag/BAcTag'
import $classes from './BAcTagList.module.scss'

export const BAcTagList = defineComponent({
  name: 'BAcTagList',
  props: {
    limit: {
      type: Number,
    },
    tagItems: {
      type: Array as PropType<IBAcTag[]>,
      default: () => [],
    },
    canClose: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array as PropType<IBAcTag[]>,
      default: () => [],
    },
    tagType: {
      type: String as PropType<tagType>,
      default: 'normal',
    },
    isClickAble: {
      type: Boolean,
      default: true,
    },
    disableLabel: {
      type: String,
      default: '',
    },
    /** 全球是否展示flag */
    showGlobalFlag: {
      type: Boolean,
      default: false,
    },
    useFullName: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { locale, fieldLang } = useLocale()
    const selectedStages = ref<string[]>([])

    const handleClick = (item: IBAcTag) => {
      if (!props.isClickAble) return

      // 处理有子项的情况
      if (item.child?.length) {
        handleSelectAll(item)

        // 对于 highestPhaseItems 类型，需要移除 child 属性
        const emitItem = item.type === 'highestPhaseItems' ? { ...item, child: undefined } : item

        emit('click', emitItem)
      } else {
        // 处理无子项的情况
        emit('click', item)
      }
    }

    const handleStageChange = (stage: IBAcTag) => {
      const index = selectedStages.value.indexOf(stage.id)
      const parentItem = props.tagItems.find((item) => item.child?.some((child) => child.id === stage.id))

      if (index === -1) {
        // 添加stage.id到selectedStages
        selectedStages.value.push(stage.id)

        if (parentItem?.child) {
          const allChildrenSelected = parentItem.child.every((child) => selectedStages.value.includes(child.id))

          if (allChildrenSelected) {
            // 所有子节点都被选中时，触发父节点（不带child）
            const newParentItem = { ...parentItem, child: undefined }
            emit('click', newParentItem)

            // 触发已在selectedItems中的子节点
            parentItem.child.forEach((child) => {
              const isChildSelected = props.selectedItems.some((item) => item.id === child.id)
              if (isChildSelected) {
                // 中英文的是父节点和子节点组合：(parentItem.display_name_en,stage.display_name_en)
                emit('click', {
                  id: child.id,
                  display_name_en: `${parentItem.display_name_en}(${child.display_name_en})`,
                  display_name_cn: `${parentItem.display_name_cn}(${child.display_name_cn})`,
                })
              }
            })
          } else {
            // 不是所有子节点都被选中时，只触发当前stage
            // 中英文的是父节点和子节点组合：(parentItem.display_name_en,stage.display_name_en)
            emit('click', {
              id: stage.id,
              display_name_en: `${parentItem.display_name_en}(${stage.display_name_en})`,
              display_name_cn: `${parentItem.display_name_cn}(${stage.display_name_cn})`,
            })
          }
        }
      } else {
        // 从selectedStages中移除stage.id
        selectedStages.value.splice(index, 1)

        if (parentItem?.child) {
          const isParentSelected = props.selectedItems.some((item) => item.id === parentItem.id)

          // 如果父节点在selectedItems中，触发父节点
          if (isParentSelected) {
            emit('click', parentItem)
          }

          // 处理父节点的所有子节点
          parentItem.child.forEach((child) => {
            const isChildInSelectedStages = selectedStages.value.includes(child.id)
            const isChildInSelectedItems = props.selectedItems.some((item) => item.id === child.id)

            // 触发需要更新的子节点
            if ((isChildInSelectedStages && !isChildInSelectedItems) || (isChildInSelectedItems && !isChildInSelectedStages)) {
              emit('click', {
                id: child.id,
                display_name_en: `${parentItem.display_name_en}(${child.display_name_en})`,
                display_name_cn: `${parentItem.display_name_cn}(${child.display_name_cn})`,
              })
            }
          })
        }
      }
    }

    const handleSelectAll = (item: IBAcTag) => {
      const childIds = item.child?.map((stage) => stage.id) || []
      const allChildrenSelected = childIds.every((id) => selectedStages.value.includes(id))

      if (allChildrenSelected) {
        // 如果所有子节点都被选中，移除它们
        selectedStages.value = selectedStages.value.filter((id) => !childIds.includes(id))
      } else {
        // 如果不是所有子节点都被选中，添加缺失的
        const newSelectedIds = childIds.filter((id) => !selectedStages.value.includes(id))
        selectedStages.value = [...selectedStages.value, ...newSelectedIds]
      }

      // 触发已在selectedItems中的子节点
      item.child?.forEach((child) => {
        if (props.selectedItems.some((item) => item.id === child.id)) {
          emit('click', child)
        }
      })
    }
    // watch selectedItems中id是all,clear selectedStages
    watch(
      () => props.selectedItems,
      () => {
        if (props.selectedItems.some((item) => item.id === 'All')) {
          selectedStages.value = []
        }
        // props.tagItems,selectedstages添加child的id
        props.selectedItems.forEach((item) => {
          const originDatas = props.tagItems.find((data) => data.id === item.id)
          if (originDatas && originDatas.child) {
            selectedStages.value.push(...(originDatas.child?.map((child) => child.id) || []))
          } else {
            // 如果是item.props.tagItems,selectedstages就添加item.id
            if (props.tagItems.some((data) => data.child?.some((child) => child.id === item.id))) {
              selectedStages.value.push(item.id)
            }
          }
          // 去掉重复的id
          selectedStages.value = [...new Set(selectedStages.value)]
        })
      },
      { deep: true, immediate: true }
    )

    return {
      locale,
      fieldLang,
      handleClick,
      selectedStages,
      handleStageChange,
      handleSelectAll,
    }
  },
  methods: {
    renderSlot() {
      return [<div slot="more">{this.$t('common.more')}</div>, <div slot="less">{this.$t('common.less')}</div>]
    },

    renderCountryFlag(item: IBAcTag, ownerClass: string) {
      const isFakeData = ['All', 'Other'].includes(item.id)
      const name = isFakeData ? item[`display_name_${this.fieldLang}`] || item.display_name_en : item.display_name_en
      return (
        <div class={[`${ownerClass} ${$classes.flag}`]} onClick={() => this.handleClick(item)}>
          <GCountryFlag useFullName={this.useFullName} name={name} showName={!isFakeData} isSmall showGlobalFlag={this.showGlobalFlag}></GCountryFlag>
        </div>
      )
    },

    renderCommonTag(item: IBAcTag, ownerClass: string) {
      return (
        <BAcTag
          ownerClass={ownerClass}
          name={getLangDegraded(item, this.locale)}
          icon={item.icon}
          canClose={this.canClose}
          onClick={() => this.handleClick(item)}
          infoContent={item.infoContent}
        >
          {this.$slots.default && this.$slots.default}
        </BAcTag>
      )
    },

    renderOptionCheckboxTag(item: IBAcTag, _ownerClass: string) {
      const isSelected = this.selectedItems.find((o) => o.id === item.id)
      // 如果selectedStages中包含item.child的id，则ownerClass为selectedTag,给出包含的数字
      const isIncludeChildId = item.child?.some((child) => this.selectedStages.includes(child.id))
      const includeChildIdCount = item.child?.filter((child) => this.selectedStages.includes(child.id)).length
      const ownerClass = isSelected || isIncludeChildId ? $classes.selectedTag : this.isClickAble ? $classes.tagAble : $classes.tagDisable
      const tagName = isIncludeChildId ? `${getLangDegraded(item, this.locale)}(${includeChildIdCount})` : getLangDegraded(item, this.locale)
      return (
        <ElPopover placement="bottom-start" trigger="hover" popper-class={$classes.tagPopper} width="200">
          <BAcTag
            slot="reference"
            ownerClass={ownerClass}
            name={tagName}
            icon={'SolidArrowDown'}
            iconPlacement="right"
            canClose={this.canClose}
            onClick={() => this.handleClick(item)}
            infoContent={item.infoContent}
          >
            {this.$slots.default && this.$slots.default}
          </BAcTag>
          <div class={$classes.checkboxContainer}>
            {item.child?.map((stage) => (
              <div key={stage.id} class="mb-1">
                <ElCheckbox value={this.selectedStages.includes(stage.id)} onChange={() => this.handleStageChange(stage)}>
                  {getLangDegraded(stage, this.locale)}
                </ElCheckbox>
              </div>
            ))}
          </div>
        </ElPopover>
      )
    },

    renderTag(item: IBAcTag) {
      const isSelected = this.selectedItems.find((o) => o.id === item.id)
      const ownerClass = isSelected ? $classes.selectedTag : this.isClickAble ? $classes.tagAble : $classes.tagDisable
      const needChildrenCheckbox = (item as IBAcTag).child?.length && item.type === 'highestPhaseItems'
      return (
        <GTooltip
          popperClass={$classes.tagPopper}
          placement="top"
          content={this.disableLabel}
          openDelay={1000}
          disabled={!this.isClickAble && this.disableLabel ? false : true}
        >
          {this.tagType === 'countryFlag'
            ? this.renderCountryFlag(item as IBAcTag, ownerClass)
            : needChildrenCheckbox
            ? this.renderOptionCheckboxTag(item as IBAcTag, ownerClass)
            : this.renderCommonTag(item as IBAcTag, ownerClass)}
        </GTooltip>
      )
    },

    renderLimitComp() {
      return (
        <GLimited
          mode="expand"
          items={this.tagItems}
          limit={this.limit}
          inline={true}
          gap={{ x: 0, y: 0 }}
          class={$classes.tagListContainer}
          scopedSlots={{
            default: ({ item }: { item: IBAcTag }) => this.renderTag(item),
          }}
        >
          {this.renderSlot()}
        </GLimited>
      )
    },

    renderAll() {
      return <div class={$classes.tagListContainer}>{[this.tagItems.map((item: IBAcTag) => this.renderTag(item)), this.$slots.more]}</div>
    },

    renderData() {
      return this.limit ? this.renderLimitComp() : this.renderAll()
    },
  },
  render() {
    const ele = this.renderData() as JSX.Element
    return ele
  },
})
